import React, { useContext } from "react"
import { getImage, GatsbyImage, withArtDirection } from "gatsby-plugin-image"
import tw, { styled } from "twin.macro"
import { ACTIONS, GlobalDispatchContext } from "../../utils/global-context"
import HiTitle from "../atoms/hi-title"
import AppIcon from "../atoms/icon"
import RippleButton from "../atoms/ripple-button"
import { graphql } from "gatsby"

const mqs = {
  poster_1: "(max-aspect-ratio: 2/3)",
  poster_2: "(max-aspect-ratio: 16/10) and (min-width: 768px)",
  poster_3: "(min-aspect-ratio: 28/11)",
}

const HeroImageWrapper = styled.div`
  position: relative;
  padding-bottom: ${(6 / 13) * 100}%;

  @media ${mqs.poster_1} {
    padding-bottom: 100%;
  }

  @media ${mqs.poster_2} {
    padding-bottom: ${(9 / 16) * 100}%;
  }

  @media ${mqs.poster_3} {
    padding-bottom: ${(3 / 8) * 100}%;
  }
`

const HeroImage = styled(GatsbyImage)`
  ${tw`!absolute !inset-0`}
`

const Hero = ({ data }) => {
  const images = withArtDirection(getImage(data.poster.localFile), [
    {
      media: mqs.poster_1,
      image: getImage(data.poster_1.localFile),
    },
    {
      media: mqs.poster_2,
      image: getImage(data.poster_2.localFile),
    },
    {
      media: mqs.poster_3,
      image: getImage(data.poster_3.localFile),
    },
  ])
  const dispatch = useContext(GlobalDispatchContext)

  return (
    <div tw="relative" className="tear-inset-b-light">
      <HeroImageWrapper className="half-tone">
        <HeroImage image={images} alt={data.poster.alternativeText} />
      </HeroImageWrapper>
      {(data.title || data.subtitle) && (
        <div
          tw="absolute bottom-0 left-0 w-full p-4 sm:(pl-8 pb-8) md:(pl-12 pb-12) xl:(pl-16 pb-16)"
          className="gradient-bottom-before"
        >
          {data.title &&
            data.title.split(" -- ").map(part => (
              <HiTitle
                key={part}
                tw="text-3xl uppercase relative sm:(text-5xl) xl:(text-6xl) xxl:text-7xl"
              >
                {part}
              </HiTitle>
            ))}
          {data.subtitle && (
            <HiTitle tw="text-2xl -mt-2 relative sm:(text-3xl) xl:text-4xl xxl:text-5xl">
              {data.subtitle}
            </HiTitle>
          )}
        </div>
      )}
      {data.vimeo_url && (
        <button
          onClick={() =>
            dispatch({
              type: ACTIONS.setVideo,
              payload: { url: data.vimeo_url, ...data.vimeo_data },
            })
          }
          tw="absolute inset-0 flex w-full items-center justify-center text-brand-green text-opacity-25"
        >
          <RippleButton
            as="span"
            tw="rounded-full transition-transform border-4 bg-black bg-opacity-25 block p-2 border-brand-green xxl:p-4"
            scale={1.05}
          >
            <AppIcon
              name="play_arrow"
              tw="w-20 h-20 block text-brand-green xl:(w-24 h-24) xxl:(w-28 h-28)"
            />
          </RippleButton>
        </button>
      )}
    </div>
  )
}

export const fragment = graphql`
  fragment Hero on FT_ComponentMoleculesHero {
    title
    subtitle
    vimeo_url
    vimeo_data {
      ...VideoData
    }
    poster: poster {
      url
      alternativeText
      localFile {
        childImageSharp {
          gatsbyImageData(aspectRatio: 2.16666667, formats: [AUTO, WEBP, AVIF])
        }
      }
    }
    poster_1: poster {
      url
      alternativeText
      localFile {
        childImageSharp {
          gatsbyImageData(aspectRatio: 1, formats: [AUTO, WEBP, AVIF])
        }
      }
    }
    poster_2: poster {
      url
      alternativeText
      localFile {
        childImageSharp {
          gatsbyImageData(aspectRatio: 1.77777778, formats: [AUTO, WEBP, AVIF])
        }
      }
    }
    poster_3: poster {
      url
      alternativeText
      localFile {
        childImageSharp {
          gatsbyImageData(aspectRatio: 2.66666667, formats: [AUTO, WEBP, AVIF])
        }
      }
    }
  }
`

export default Hero
